import { supabase } from "@/integrations/supabase/client";

export async function fetchAccounts(householdId: string) {
  const { data, error } = await supabase.from("accounts").select("*").eq("household_id", householdId).eq("is_active", true).order("name");
  if (error) throw error;
  return data ?? [];
}

export async function fetchAllAccounts(householdId: string) {
  const { data, error } = await supabase.from("accounts").select("*").eq("household_id", householdId).order("name");
  if (error) throw error;
  return data ?? [];
}

export async function fetchCategories(householdId: string) {
  const { data, error } = await supabase.from("categories").select("*").eq("household_id", householdId).eq("is_active", true).order("name");
  if (error) throw error;
  return data ?? [];
}

export async function fetchTags(householdId: string) {
  const { data } = await supabase.from("tags").select("*").eq("household_id", householdId).order("name");
  return data ?? [];
}

export async function fetchCcDetails(accountId: string) {
  const { data, error } = await supabase
    .from("credit_card_details")
    .select("closing_day, due_day")
    .eq("account_id", accountId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

export async function fetchProvisions(householdId: string, opts?: { includeArchived?: boolean }) {
  let q = supabase.from("provisions_and_goals").select("*").eq("household_id", householdId);
  // Por defecto solo cajitas activas (las archivadas no deben aparecer en
  // selectores como el de movimientos). La página de Cajitas pide includeArchived.
  if (!opts?.includeArchived) q = q.is("archived_at", null);
  const { data } = await q.order("name");
  return data ?? [];
}

export async function fetchMembers(householdId: string) {
  const { data } = await supabase.from("profiles").select("id, display_name").eq("household_id", householdId);
  return data ?? [];
}

export interface MonthRange { startDate: string; endDate: string; }
export function monthRange(year: number, month: number): MonthRange {
  const start = new Date(Date.UTC(year, month - 1, 1));
  const end = new Date(Date.UTC(year, month, 0));
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return { startDate: fmt(start), endDate: fmt(end) };
}

/**
 * Columnas de `transaction_headers` seguras para un `select` de lista/edición (todas menos
 * `refund_of_header_id`). Rebote #170 ronda 3, FIX 2: esa columna puede apuntar a una compra que
 * quien lee no ve — publicarla en un `select("*")` filtra el id igual que si no hubiera RLS
 * (familia #164). El vínculo (enmascarado por visibilidad) se pide aparte con
 * `fetchBankRefundLinksFor` (`bank_refund_link_for`, DEFINER).
 *
 * Rebote #170 ronda 4, FIX 1: esta lista sola no alcanzaba — sacaba la columna de lo que pide el
 * FRONTEND, pero no de lo que la base ENTREGA (`select refund_of_header_id`, `select *`,
 * `to_jsonb(h)` seguían devolviéndola). La migración `20260927060203_transaction-headers-column-grants.sql`
 * saca el GRANT SELECT a nivel de TABLA para `authenticated`/`anon` y lo vuelve a dar columna por
 * columna, sin `refund_of_header_id`. Si el día de mañana se agrega una columna a la tabla, hay
 * que sumarla ACÁ y darle su `GRANT SELECT (col) ON public.transaction_headers TO authenticated`
 * en una migración — un `ALTER TABLE ADD COLUMN` no hereda un GRANT column-level existente.
 *
 * Rebote #170 ronda 5: sin ese GRANT, TODO `select` que pida esta lista falla con
 * `permission denied` (no sólo la columna nueva). Eso rompe PROD, no la evidencia local (tsc,
 * test y build no tocan la DB), y hasta esta ronda los lectores de abajo lo convertían en
 * "0 movimientos". Lo que lo impide es `src/lib/__tests__/transactionHeaderGrants.test.ts`:
 * reconstruye los GRANT desde `supabase/migrations/` y falla si esta lista y los GRANT no
 * coinciden, o si una migración agrega una columna a la tabla sin su GRANT.
 *
 * V1a (Decisión 36): `verified_at` suma con su propio GRANT SELECT (col). `verified_statement_period_id`
 * NO se agrega acá — sin GRANT a propósito (una línea shared puede venir del extracto de la
 * cuenta personal del otro, familia #164/#170); se lee aparte con un DEFINER cuando haga falta.
 */
export const TRANSACTION_HEADER_SAFE_COLUMNS =
  "id,household_id,created_by,account_id,related_account_id,type,date,description,total_amount,exchange_rate,created_at,debtor_id,creditor_id,settled_at,settled_with_header_id,settle_method,accounting_only,is_refund,is_income_record,source_cc_payment_id,split_ratio_snapshot,source_bank_expense_id,currency,source,agent_confidence,agent_message_id,source_cc_purchase_id,purpose,household_card_account_id,verified_at,bank_description" as const;

export async function fetchTransactionsWithLines(householdId: string, range: MonthRange) {
  const { data: headers, error: headersError } = await supabase
    .from("transaction_headers")
    .select(TRANSACTION_HEADER_SAFE_COLUMNS)
    .eq("household_id", householdId)
    .gte("date", range.startDate)
    .lte("date", range.endDate)
    .order("date", { ascending: false })
    .order("created_at", { ascending: false });
  if (headersError) throw headersError;
  const ids = (headers ?? []).map((h) => h.id);
  const { data: lines, error: linesError } = ids.length
    ? await supabase.from("transaction_lines").select("*").in("header_id", ids)
    : { data: [], error: null };
  if (linesError) throw linesError;
  return { headers: headers ?? [], lines: lines ?? [] };
}

/**
 * Fetch headers + lines en un rango opcional, con paginación.
 * - Si `range` es null/undefined → no acota por fechas (usado por "all-time").
 * - `limit` controla cuántos headers traer (default 200).
 * - `offset` para "cargar más".
 * Devuelve también `total` (count exacto) para saber si hay más.
 */
export async function fetchTransactionsRange(
  householdId: string,
  range: MonthRange | null,
  limit = 200,
  offset = 0,
) {
  let q = supabase
    .from("transaction_headers")
    .select(TRANSACTION_HEADER_SAFE_COLUMNS, { count: "exact" })
    .eq("household_id", householdId);
  if (range) {
    q = q.gte("date", range.startDate).lte("date", range.endDate);
  }
  const {
    data: headers,
    count,
    error: headersError,
  } = await q
    .order("date", { ascending: false })
    .order("created_at", { ascending: false })
    .range(offset, offset + limit - 1);
  if (headersError) throw headersError;
  const ids = (headers ?? []).map((h) => h.id);
  const { data: lines, error: linesError } = ids.length
    ? await supabase.from("transaction_lines").select("*").in("header_id", ids)
    : { data: [], error: null };
  if (linesError) throw linesError;
  return { headers: headers ?? [], lines: lines ?? [], total: count ?? 0 };
}

export async function fetchLastExchangeRate(householdId: string, fromAccountId: string, toAccountId: string): Promise<number | null> {
  const { data } = await supabase
    .from("transaction_headers")
    .select("exchange_rate")
    .eq("household_id", householdId)
    .eq("type", "transfer")
    .eq("account_id", fromAccountId)
    .eq("related_account_id", toAccountId)
    .order("date", { ascending: false })
    .limit(1);
  const r = data?.[0]?.exchange_rate;
  return r ? Number(r) : null;
}

/**
 * Hard-delete de un movimiento (header + líneas). Solo el creador puede ejecutarlo.
 * Recalcula los saldos de las cuentas afectadas y libera recurrencias.
 */
export async function deleteTransaction(headerId: string): Promise<void> {
  const { error } = await supabase.rpc("delete_transaction" as any, { _header_id: headerId } as any);
  if (error) throw error;
}
